import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Send, MessageCircle, Paperclip, X, Loader2, Zap, Smile } from 'lucide-react';
import { apiFetch } from '../lib/api';
import { smartCompressImage } from '../lib/imageCompression';

export interface ChatMessage {
  id: number;
  direction?: string;
  message: string;
  messageType?: string;
  mediaUrl?: string | null;
  status?: string;
  createdAt: number;
  sender?: 'user' | 'bot';
  /** 出站來源：'ai'＝AI 助手自動回覆（🤖 標記）；'human'＝管理員手動；其他＝系統 */
  origin?: string;
}

export interface CsTemplateLite {
  id: number;
  title: string;
  body: string;
  category: string;
  use_count: number;
}

interface WhatsAppChatPanelProps {
  phone: string;
  /** 外層容器高度（如 h-[calc(100vh-240px)]）；唔填就用 max-h-70vh */
  heightClass?: string;
  inputPlaceholder?: string;
  /** CS 話術庫（⚡ picker 用；唔傳＝無話術掣） */
  csTemplates?: CsTemplateLite[];
  /** 發送完成後回調（父層刷新清單用） */
  onSent?: () => void;
}

const ACCEPTED_TYPES = 'image/jpeg,image/png,image/webp,image/gif';

const EMOJIS = [
  '😀','😁','😂','🤣','😊','😍','🥰','😘','😎','🤔','😅','🙃','😴','😭','😡','🥳',
  '🤩','🙏','👍','👌','✌️','🤝','💪','🌶️','🔥','❤️','💛','💚','✨','🎉','🎁','📦',
  '✅','❌','💰','🛒','📷','📅','⏰','🚚','📍','🙇','😅','😳','😋','🤤',
];

/**
 * WhatsApp 客戶對話面板（/admin/conversations 與 /admin/orders/:id 共用）。
 * 工具列一行：📎 圖片｜😊 emoji｜⚡ 話術（chip 點開預覽全文→使用）——唔預留空間。
 * 附件必須 JPEG 壓縮（WebP 經 WhatsApp 會被當 sticker 靜默消失）。
 */
const WhatsAppChatPanel: React.FC<WhatsAppChatPanelProps> = ({
  phone,
  heightClass = 'max-h-[70vh]',
  inputPlaceholder = '輸入回覆訊息... (按 Enter 發送)',
  csTemplates,
  onSent,
}) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [replyText, setReplyText] = useState('');
  const [sendLoading, setSendLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);
  // A・修正認知（需求 A）：correctingId＝編輯緊嘅訊息；corrState 'saved' 短暫顯示成功
  const [correctingId, setCorrectingId] = useState<number | null>(null);
  const [corrText, setCorrText] = useState('');
  const [corrSaving, setCorrSaving] = useState(false);
  const [corrState, setCorrState] = useState<'idle' | 'saved' | 'error'>('idle');
  const [showEmoji, setShowEmoji] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [previewTpl, setPreviewTpl] = useState<CsTemplateLite | null>(null);
  const [previewDraft, setPreviewDraft] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  // 輪詢期間保留閱讀位置：只有本來貼近底部先自動跟落去（否則留喺原位繼續讀舊訊息）
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const wasNearBottomRef = useRef(true);

  const fetchMessages = useCallback(async () => {
    try {
      const res = await apiFetch(`/api/public/admin/whatsapp/conversations/${phone}`);
      if (res.ok) {
        const json = await res.json();
        const data = json.data ?? json;
        const list: ChatMessage[] = (data.messages || []).map((m: any) => ({
          ...m,
          sender: m.direction === 'outbound' || m.sender === 'bot' ? 'bot' : 'user',
        }));
        setMessages(list);
      } else if (res.status === 401) {
        localStorage.removeItem('admin_token');
        window.location.href = '/admin';
      }
    } catch (e) {
      console.error('Failed to fetch messages:', e);
    } finally {
      setLoading(false);
    }
  }, [phone]);

  useEffect(() => {
    setLoading(true);
    setShowEmoji(false);
    setShowTemplates(false);
    setPreviewTpl(null);
    wasNearBottomRef.current = true; // 揀新對話→由底部開始睇最新訊息
    fetchMessages();
  }, [fetchMessages]);

  useEffect(() => {
    if (messagesEndRef.current && wasNearBottomRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // 15 秒輪詢新訊息（tab 隱藏時暫停）——客人回覆自動浮現，唔使手動刷新
  useEffect(() => {
    const id = setInterval(() => {
      if (!document.hidden) fetchMessages();
    }, 15_000);
    return () => clearInterval(id);
  }, [fetchMessages]);

  // ── AI 接手狀態（conversation_state.ai_handoff；無記錄＝預設 AI 接手）──
  const [aiHandoff, setAiHandoff] = useState<boolean | null>(null);
  const [aiToggling, setAiToggling] = useState(false);
  useEffect(() => {
    let alive = true;
    setAiHandoff(null);
    apiFetch(`/api/public/admin/ai-assistant/handoff/${phone}`)
      .then((r) => r.json())
      .then((j) => {
        const d = j.data ?? j;
        if (alive) setAiHandoff(d.aiHandoff !== false);
      })
      .catch(() => { if (alive) setAiHandoff(true); });
    return () => { alive = false; };
  }, [phone]);

  const toggleAi = async () => {
    if (aiToggling || aiHandoff === null) return;
    setAiToggling(true);
    try {
      const res = await apiFetch(`/api/public/admin/ai-assistant/handoff/${phone}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ aiHandoff: aiHandoff === false }),
      });
      if (res.ok) setAiHandoff(aiHandoff === false);
    } finally {
      setAiToggling(false);
    }
  };

  const clearFile = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setSelectedFile(null);
    setPreviewUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setSendError(null);
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(file.type)) {
      setSendError('只支援 JPG / PNG / WebP / GIF 圖片');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setSendError('圖片太大（上限 10MB）');
      return;
    }
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const handleSendMessage = async () => {
    if (!phone || sendLoading) return;
    const text = replyText.trim();
    if (!text && !selectedFile) return;

    setSendLoading(true);
    setSendError(null);
    try {
      let imageUrl: string | undefined;
      if (selectedFile) {
        // WhatsApp 鐵律：經 WhatsApp 發送嘅圖必須 JPEG（WebP 被當 sticker 靜默消失）
        const compressed = await smartCompressImage(selectedFile, { format: 'jpeg' });
        const fd = new FormData();
        fd.append('file', compressed.file);
        fd.append('category', 'general');
        const upRes = await apiFetch('/api/public/admin/media/upload', { method: 'POST', body: fd });
        const upJson = await upRes.json().catch(() => ({}));
        if (!upRes.ok || !upJson.url) {
          throw new Error(upJson.error || '圖片上載失敗');
        }
        imageUrl = upJson.url;
      }

      const res = await apiFetch('/api/public/admin/whatsapp/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone, message: text, ...(imageUrl ? { imageUrl } : {}) }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || data.detail || '發送失敗，請稍後再試');
      }
      setReplyText('');
      clearFile();
      setShowEmoji(false);
      setShowTemplates(false);
    } catch (e) {
      setSendError(e instanceof Error ? e.message : '發送失敗：無法連線到伺服器');
    } finally {
      setSendLoading(false);
      // 成功定失敗都刷新：失敗訊息（status=failed）都會出現喺對話記錄
      await fetchMessages();
      onSent?.();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const useTemplate = async (body: string, tplId?: number) => {
    setReplyText(body);
    setPreviewTpl(null);
    setShowTemplates(false);
    if (tplId) apiFetch(`/api/public/admin/cs-templates/${tplId}/use`, { method: 'POST' }).catch(() => {});
  };

  const hasTemplates = (csTemplates?.length || 0) > 0;

  return (
    <div className={`flex flex-col ${heightClass} bg-gray-50 dark:bg-gray-900 min-h-0`}>
      {/* AI 接手狀態列：撳一下切換 AI 自動回覆／人工接手（寫 conversation_state.ai_handoff） */}
      <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-xl px-3 py-1.5">
        <button
          onClick={toggleAi}
          disabled={aiToggling || aiHandoff === null}
          title="切換：🤖 AI 接手（關鍵字冇命中嘅一般查詢自動回覆）↔ 🙋 人工接手（AI 對此客靜音）"
          className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold transition disabled:opacity-50 ${
            aiHandoff === false
              ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
              : 'bg-gray-100 text-gray-500 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300'
          }`}
        >
          {aiToggling ? '切換中…' : aiHandoff === false ? '🙋 人工接手中（AI 靜音）' : '🤖 AI 接手中'}
        </button>
        <span className="text-[10px] text-gray-400 dark:text-gray-500">撳一下切換 AI／人工</span>
      </div>
      {/* Messages */}
      <div
        ref={scrollContainerRef}
        onScroll={() => {
          const el = scrollContainerRef.current;
          if (el) wasNearBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
        }}
        className="flex-1 overflow-y-auto p-4 space-y-3 min-h-0"
      >
        {loading ? (
          <p className="text-center text-gray-400 dark:text-gray-500 py-8 text-sm">加載對話中...</p>
        ) : messages.length === 0 ? (
          <div className="text-center py-8">
            <MessageCircle className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
            <p className="text-gray-400 dark:text-gray-500 text-sm">暫無對話記錄</p>
            <p className="text-gray-400 dark:text-gray-500 text-xs mt-1">客戶發送消息後會自動顯示在此</p>
          </div>
        ) : (
          messages.map((msg, mi) => {
            // A・修正認知（需求 A）：AI 回覆上一則客人輸入＝問題原文
            const prevInbound = [...messages.slice(0, mi)].reverse().find((m) => m.direction === 'inbound' || m.sender === 'user');
            return (
            <div key={msg.id} className={`flex ${msg.sender === 'bot' ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`max-w-[85%] p-3 rounded-2xl text-sm ${
                  msg.sender === 'bot'
                    ? 'bg-red-600 text-white rounded-br-none'
                    : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-800 dark:text-gray-200 rounded-bl-none'
                }`}
              >
                {msg.messageType === 'image' && msg.mediaUrl && (
                  <a href={msg.mediaUrl} target="_blank" rel="noopener noreferrer" className="block mb-1">
                    <img
                      src={msg.mediaUrl}
                      alt="圖片"
                      className="max-w-[240px] w-full aspect-[4/3] object-cover rounded-lg border border-black/10"
                    />
                  </a>
                )}
                {msg.message && <div className="break-words whitespace-pre-wrap">{msg.message}</div>}
                <div className={`text-xs mt-1 ${msg.sender === 'bot' ? 'text-red-200' : 'text-gray-400 dark:text-gray-500'}`}>
                  {msg.status === 'failed' && <span className="mr-1 font-semibold">⚠ 發送失敗</span>}
                  {msg.origin === 'ai' && <span className="mr-1 font-semibold" title="AI 助手自動回覆">🤖</span>}
                  {msg.createdAt ? new Date(msg.createdAt * 1000).toLocaleString('zh-HK', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                </div>
                {/* A・修正認知：AI 回覆加「修正」掣 → 修正內容入 RAG 語料 */}
                {msg.origin === 'ai' && msg.message && (
                  <div className="mt-1">
                    {correctingId === msg.id ? (
                      <div className="rounded-lg bg-white/95 p-2 text-gray-800">
                        <p className="text-[10px] mb-1 text-gray-500">客人問題：{(prevInbound?.message || '（對話中搵唔到原問）').slice(0, 80)}</p>
                        <textarea
                          autoFocus
                          rows={3}
                          value={corrText}
                          onChange={(e) => setCorrText(e.target.value)}
                          placeholder="輸入正確／更佳嘅回覆內容——會成為小辣嘅教材"
                          className="w-full text-xs p-1.5 border border-gray-300 rounded resize-none"
                        />
                        <div className="flex items-center gap-2 mt-1">
                          <button
                            onClick={async () => {
                              if (!corrText.trim() || corrSaving) return;
                              setCorrSaving(true);
                              try {
                                const res = await apiFetch('/api/public/admin/ai-corrections', {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ phone, question: prevInbound?.message || '', originalAnswer: msg.message, correctedAnswer: corrText.trim() }),
                                });
                                if (res.ok) { setCorrState('saved'); setCorrectingId(null); setCorrText(''); setTimeout(() => setCorrState('idle'), 4000); }
                                else setCorrState('error');
                              } catch { setCorrState('error'); } finally { setCorrSaving(false); }
                            }}
                            disabled={!corrText.trim() || corrSaving}
                            className="text-[10px] font-bold px-2 py-0.5 rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                          >
                            {corrSaving ? '儲存中…' : '提交修正'}
                          </button>
                          <button onClick={() => { setCorrectingId(null); setCorrText(''); }} className="text-[10px] text-gray-500 hover:text-gray-700">取消</button>
                          {corrState === 'error' && <span className="text-[10px] text-red-600">儲存失敗，請重試</span>}
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => { setCorrectingId(correctingId === msg.id ? null : msg.id); setCorrText(''); setCorrState('idle'); }}
                          className="text-[10px] text-red-100/80 hover:text-white underline underline-offset-2"
                          title="AI 呢句講錯／可以更好？修正佢——內容會入 RAG 語料教好小辣"
                        >
                          ✏️ 修正認知
                        </button>
                        {corrState === 'saved' && <span className="text-[10px] text-green-100 font-bold">✓ 已入 RAG 語料</span>}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Reply Input */}
      <div className="border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-b-xl">
        {sendError && (
          <div className="mx-3 mt-2 p-2.5 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-300">
            <span className="font-semibold">發送失敗：</span>{sendError}
          </div>
        )}
        {selectedFile && previewUrl && (
          <div className="mx-3 mt-2 flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
            <img src={previewUrl} alt="預覽" className="w-12 h-12 object-cover rounded" />
            <div className="flex-1 min-w-0 text-xs text-gray-600 dark:text-gray-300 truncate">
              {selectedFile.name}（將以 JPEG 發送）
            </div>
            <button onClick={clearFile} className="p-1 text-gray-400 hover:text-red-600" title="移除圖片">
              <X size={16} />
            </button>
          </div>
        )}
        {/* Emoji picker：工具列上方浮出，唔佔常駐空間 */}
        {showEmoji && (
          <div className="mx-3 mt-2 p-2 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 grid grid-cols-12 gap-0.5 max-h-32 overflow-y-auto">
            {EMOJIS.map((em, i) => (
              <button
                key={`${em}-${i}`}
                onClick={() => setReplyText((prev) => prev + em)}
                className="text-xl leading-7 hover:bg-gray-200 dark:hover:bg-gray-700 rounded"
              >
                {em}
              </button>
            ))}
          </div>
        )}
        {/* ⚡ 話術 chips：開啟時直接出現喺輸入區上方（無需預留，直接填滿） */}
        {showTemplates && hasTemplates && (
          <div className="flex gap-2 overflow-x-auto px-3 py-2">
            {csTemplates!.map((t) => (
              <button
                key={t.id}
                onClick={() => { setPreviewTpl(t); setPreviewDraft(t.body); }}
                className="shrink-0 rounded-full border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 dark:border-red-800 dark:bg-gray-800 dark:text-red-300"
                title={`${t.category}｜撳開預覽全文`}
              >
                ⚡ {t.title}
              </button>
            ))}
          </div>
        )}
        <div className="p-2 space-y-1.5">
          {/* 快捷掣＋發送同一行（喺輸入框上方） */}
          <div className="flex items-center gap-1">
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_TYPES}
              onChange={handleFileChange}
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={sendLoading}
              title="附加圖片"
              className={`p-2 rounded-lg transition-colors disabled:opacity-50 ${selectedFile ? 'text-red-600 bg-red-50 dark:bg-red-900/20' : 'text-gray-500 dark:text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20'}`}
            >
              <Paperclip className="w-5 h-5" />
            </button>
            <button
              onClick={() => { setShowEmoji((v) => !v); setShowTemplates(false); }}
              disabled={sendLoading}
              title="Emoji"
              className={`p-2 rounded-lg transition-colors disabled:opacity-50 ${showEmoji ? 'text-red-600 bg-red-50 dark:bg-red-900/20' : 'text-gray-500 dark:text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20'}`}
            >
              <Smile className="w-5 h-5" />
            </button>
            {hasTemplates && (
              <button
                onClick={() => { setShowTemplates((v) => !v); setShowEmoji(false); }}
                disabled={sendLoading}
                title="快捷話術"
                className={`p-2 rounded-lg transition-colors disabled:opacity-50 ${showTemplates ? 'text-red-600 bg-red-50 dark:bg-red-900/20' : 'text-gray-500 dark:text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20'}`}
              >
                <Zap className="w-5 h-5" />
              </button>
            )}
            <span className="flex-1" />
            <button
              onClick={handleSendMessage}
              disabled={sendLoading || (!replyText.trim() && !selectedFile)}
              className="bg-red-600 hover:bg-red-700 disabled:bg-red-300 dark:disabled:bg-red-900/50 text-white p-2 rounded-lg transition-colors"
            >
              {sendLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
            </button>
          </div>
          <textarea
            value={replyText}
            onChange={e => {
              setReplyText(e.target.value);
              setSendError(null);
              e.target.style.height = 'auto';
              e.target.style.height = Math.min(e.target.scrollHeight, 110) + 'px';
            }}
            onKeyDown={handleKeyDown}
            placeholder={inputPlaceholder}
            rows={1}
            className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none resize-none bg-white dark:bg-gray-800 dark:text-white"
            disabled={sendLoading}
          />
        </div>
      </div>

      {/* 話術預覽＋直接編輯 modal（改完即用，唔使翻嚟翻去） */}
      {previewTpl && (
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4" onClick={() => setPreviewTpl(null)}>
          <div className="w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl bg-white dark:bg-gray-800" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3 dark:border-gray-700">
              <div className="min-w-0">
                <p className="truncate font-bold text-gray-800 dark:text-white">⚡ {previewTpl.title}</p>
                <p className="text-xs text-gray-400">{previewTpl.category} · 已用 {previewTpl.use_count} 次</p>
              </div>
              <button onClick={() => setPreviewTpl(null)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <div className="px-4 py-3">
              <textarea
                value={previewDraft}
                onChange={(e) => setPreviewDraft(e.target.value)}
                rows={6}
                className="w-full resize-y rounded-lg border border-gray-300 p-2.5 text-sm outline-none focus:border-red-500 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
              />
            </div>
            <div className="flex gap-2 border-t border-gray-100 px-4 py-3 dark:border-gray-700">
              <button onClick={() => setPreviewTpl(null)} className="flex-1 rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-600 dark:border-gray-600 dark:text-gray-300">取消</button>
              <button onClick={() => useTemplate(previewDraft, previewTpl.id)} className="flex-1 rounded-lg bg-red-600 px-4 py-2 text-sm font-bold text-white hover:bg-red-700">使用</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WhatsAppChatPanel;
